import React, { useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Box, Checkbox, Divider, FormControlLabel, List, ListItem, ListItemText, Typography } from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { type ISection } from '../../../../models/TermsSection'

interface UpdateTemplateRevisionModalProps {
  selectedRow?: ISection | null
  handleCloseModal?: () => void
  onConfirm: (row?: ISection | null) => void
  isSubmitting?: boolean
}

const IMPACTS = [
  'For Report-per-Instructor survey sections, previously collected responses may not map correctly to the updated survey template.',
  "We'll do our best to migrate previously submitted responses for this section to the correct survey template revision.",
  'Reports for already closed survey projects may also be impacted.'
]

export const UpdateTemplateRevisionModal = ({
  selectedRow,
  handleCloseModal,
  onConfirm,
  isSubmitting = false
}: UpdateTemplateRevisionModalProps): JSX.Element => {
  const abbreviation = selectedRow?.linkedSection?.abbreviation ?? ''
  const title = selectedRow?.linkedSection?.title ?? ''
  const [hasAgreed, setHasAgreed] = useState(false)

  return (
    <Box>
      <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography fontWeight='bold' fontSize='0.875rem'>
          The latest published survey template revision will be applied to this course section survey.
        </Typography>
      </Alert>

      {(abbreviation !== '' || title !== '') && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant='body2' color='text.secondary'>Course Section</Typography>
            <Typography variant='body2' fontWeight={500}>{abbreviation}</Typography>
            <Typography variant='body2' color='text.secondary'>Title</Typography>
            <Typography variant='body2' fontWeight={500}>{title}</Typography>
          </Box>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
        Before you proceed, please note:
      </Typography>

      <List dense disablePadding sx={{ mb: 2 }}>
        {IMPACTS.map((impact) => (
          <ListItem key={impact} disableGutters sx={{ alignItems: 'flex-start', gap: 1 }}>
            <Typography component='span' sx={{ mt: '3px', lineHeight: 1, flexShrink: 0 }}>•</Typography>
            <ListItemText primary={impact} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>

      <FormControlLabel
        sx={{ alignItems: 'flex-start', mb: 3, ml: 0 }}
        control={
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => { setHasAgreed(e.target.checked) }}
            sx={{ pt: 0, pl: 0, mr: 1 }}
          />
        }
        label={
          <Typography variant='body2'>
            I understand these impacts and want to apply the latest survey template revision.
          </Typography>
        }
      />

      <DefaultModalActions
        destructiveButtonProps={{ onClick: handleCloseModal }}
        confirmButtonProps={{
          onClick: () => { onConfirm(selectedRow) },
          text: 'Update Revision',
          buttonType: ButtonType.Primary,
          disabled: !hasAgreed,
          loading: isSubmitting
        }}
      />
    </Box>
  )
}
